// Types AND values come from the published package the CDN serves — the merchant has nothing
// else (D-091). This import also REGISTERS the custom elements. The specifier stays EXTERNAL
// (rolldown.config.mjs) and the page's IMPORT MAP resolves it to the CDN, so the browser fetches
// ONE copy that every module shares (conventions §12: a second runtime would mean duplicated
// module state, duplicate custom-element registration, and a SessionGoneError whose identity the
// session wouldn't recognize). TypeScript maps the same name to the shipped
// payplug-elements.d.ts (tsconfig `paths`), the file an integrator actually receives.
import {
  NetworkSessionClient,
  type AttemptStartDetail,
  type MethodChangeDetail,
  type PayplugSession,
  type StatusChangeDetail,
} from "@payplug/elements";

// M11 (dalenys-provider.md lot 5) — the m8 wiring over the REAL PSP: the methods route to
// the DalenysProvider (sandbox secure-test), the card renders the provider's HOSTED FIELDS
// (the view carries the public bootstrap — C4), and post-redirect verdicts arrive through
// the real notification door (your tunnel). This script still has NO domain in it: same
// bare return leg, same one-way wiring as m8 — the page cannot tell the PSP changed.

const TOKEN_KEY = "payplug-m11:token";
const ORCH_KEY = "payplug-m11:orchestrator";

const FLAGS = [
  { id: "flag-partial", attr: "partial-payment", key: "payplug-m11:partial" },
  { id: "flag-cancel", attr: "individual-cancel", key: "payplug-m11:cancel" },
  { id: "flag-hide-cart-on-final", attr: "hide-cart-on-final", key: "payplug-m11:hide-cart-on-final" },
  { id: "flag-order-id", attr: "show-order-id-in-cart", key: "payplug-m11:show-order-id-in-cart" },
  { id: "flag-group-by-brand", attr: "group-cart-by-brand", key: "payplug-m11:group-cart-by-brand" },
] as const;

/** Whether the shop wants the payer's card registered for later merchant-initiated payments —
 *  a SESSION property, unlike the attribute flags above: it is decided when the session is
 *  created, so ticking it starts a new one. `"1"`: later payments; `"guarantee"`: a 150 € deposit
 *  (the later debit's ceiling and reason, said in the card form's consent). */
const ALIAS_KEY = "payplug-m11:alias";
/** Which payment of a two-payment order this session makes (plans/projects/done/echeancier.md): `"initial"`
 *  — the deposit, the balance to come — or `"balance"` — the deposit already paid. A SESSION property,
 *  like the alias. */
const PAYMENTS_KEY = "payplug-m11:payments";
const TERMS_OF_KEY = "payplug-m11:terms-of";

// Fetch a fresh session token from the merchant backend (this demo server).
async function createSession(): Promise<{ elementsToken: string; orchestratorUrl: string }> {
  // The page hands its OWN absolute URL to the merchant backend: only the browser knows it
  // (port mappings, tunnels…), and the PSP must send the customer back exactly there. The
  // backend still validates the origin — the returnUrl stays the merchant's contract.
  const alias = sessionStorage.getItem(ALIAS_KEY);
  const response = await fetch("./m11/session", {
    method: "POST",
    headers: { "content-type": "application/json" },
    // A distinct cancel page too — same page with a marker, enough to see WHICH of the two
    // Dalenys sends the customer back to.
    body: JSON.stringify({
      returnUrl: `${location.origin}${location.pathname}`,
      cancelUrl: `${location.origin}${location.pathname}?abandon=1`,
      mustCreateAlias: alias === "1" || alias === "guarantee",
      guarantee: alias === "guarantee",
      payments: sessionStorage.getItem(PAYMENTS_KEY),
    }),
  });
  if (!response.ok) throw new Error(`merchant backend answered ${response.status}`);
  return (await response.json()) as { elementsToken: string; orchestratorUrl: string };
}

async function main(): Promise<void> {
  // Token bootstrap: reuse the stored session across the redirect round-trip; otherwise ask
  // the merchant backend for a new one. "Nouvelle session" clears the storage.
  let token = sessionStorage.getItem(TOKEN_KEY);
  let orchestratorUrl = sessionStorage.getItem(ORCH_KEY);
  if (token === null || orchestratorUrl === null) {
    try {
      const created = await createSession();
      token = created.elementsToken;
      orchestratorUrl = created.orchestratorUrl;
      sessionStorage.setItem(TOKEN_KEY, token);
      sessionStorage.setItem(ORCH_KEY, orchestratorUrl);
    } catch (error) {
      console.error(error);
      const bootError = document.getElementById("boot-error");
      if (bootError) bootError.hidden = false;
      return;
    }
  }

  const client = new NetworkSessionClient({ baseUrl: orchestratorUrl, elementsToken: token });

  await customElements.whenDefined("payplug-session");
  const session = document.querySelector<PayplugSession>("payplug-session");
  if (!session) return;

  // Merchant feature flags: restore from storage → apply to the session + the checkboxes,
  // then toggle live on change (the session is reactive — D-072).
  for (const flag of FLAGS) {
    const checkbox = document.getElementById(flag.id);
    if (!(checkbox instanceof HTMLInputElement)) continue;
    checkbox.checked = sessionStorage.getItem(flag.key) === "1";
    session.toggleAttribute(flag.attr, checkbox.checked);
    checkbox.addEventListener("change", () => {
      session.toggleAttribute(flag.attr, checkbox.checked);
      sessionStorage.setItem(flag.key, checkbox.checked ? "1" : "0");
    });
  }

  // The terms control drives the session's `terms-of` attribute, live, like the flags above. The
  // terms themselves are declared when the session is created (server.mjs).
  const termsOf = document.getElementById("terms-of");
  if (termsOf instanceof HTMLSelectElement) {
    // "-" is the ABSENT attribute, and it is the default: a merchant who does not say which family
    // their documents belong to gets no heading rather than one we invented.
    const applyFamily = (value: string): void => {
      if (value === "") session.removeAttribute("terms-of");
      else session.setAttribute("terms-of", value);
    };
    termsOf.value = sessionStorage.getItem(TERMS_OF_KEY) ?? "";
    applyFamily(termsOf.value);
    termsOf.addEventListener("change", () => {
      applyFamily(termsOf.value);
      sessionStorage.setItem(TERMS_OF_KEY, termsOf.value);
    });
  }

  // The alias boxes are NOT an attribute of the elements but a property of the SESSION: what a
  // merchant asks for when it creates the order (aliased-payments.md). Ticking it therefore has
  // to start a new session — and the demonstration is the point, since the method list then
  // shrinks to the card that can register, live.
  //
  // Two boxes, one choice: later payments, or a 150 € deposit — ticking one unticks the other,
  // since a session registers the card once, for one reason.
  for (const [id, value] of [
    ["flag-alias", "1"],
    ["flag-alias-guarantee", "guarantee"],
  ] as const) {
    const box = document.getElementById(id);
    if (!(box instanceof HTMLInputElement)) continue;
    box.checked = sessionStorage.getItem(ALIAS_KEY) === value;
    box.addEventListener("change", () => {
      const wanted = box.checked;
      sessionStorage.clear();
      sessionStorage.setItem(ALIAS_KEY, wanted ? value : "0");
      location.href = "./m11.html";
    });
  }

  // The schedule boxes, same shape as the alias ones: a session property, so ticking one starts a new
  // session — and two boxes, one choice: the first payment of the order, or its balance.
  for (const [id, value] of [
    ["flag-payments-initial", "initial"],
    ["flag-payments-balance", "balance"],
  ] as const) {
    const box = document.getElementById(id);
    if (!(box instanceof HTMLInputElement)) continue;
    box.checked = sessionStorage.getItem(PAYMENTS_KEY) === value;
    box.addEventListener("change", () => {
      const wanted = box.checked;
      const alias = sessionStorage.getItem(ALIAS_KEY);
      sessionStorage.clear();
      if (alias !== null) sessionStorage.setItem(ALIAS_KEY, alias);
      if (wanted) sessionStorage.setItem(PAYMENTS_KEY, value);
      location.href = "./m11.html";
    });
  }

  // Merchant reaction to the single routing signal (D-053/D-072): on a terminal status, the
  // shop shows its "return to shop" affordance — the elements never render it themselves.
  const merchantReturn = document.getElementById("merchant-return");
  const merchantLabel = document.getElementById("merchant-return-label");
  const terminalLabel: Record<string, string> = {
    PAID: "Commande validée.",
    ABORTED: "Paiement abandonné.",
    EXPIRED: "Session expirée.",
  };
  // The two events an analytics tool listens to (evenements-analytics.md), in the console for Alyson to see them pass.
  session.addEventListener("payplug:methodchange", (event) => {
    console.log("payplug:methodchange", (event as CustomEvent<MethodChangeDetail>).detail);
  });
  session.addEventListener("payplug:attemptstart", (event) => {
    console.log("payplug:attemptstart", (event as CustomEvent<AttemptStartDetail>).detail);
  });
  session.addEventListener("payplug:statuschange", (event) => {
    const detail = (event as CustomEvent<StatusChangeDetail>).detail;
    console.log(`payplug:statuschange ${detail.status}`, detail);
    const label = terminalLabel[detail.status];
    if (merchantReturn) merchantReturn.hidden = label === undefined;
    if (merchantLabel && label !== undefined) merchantLabel.textContent = label;
    // The deposit can be withheld once the order is paid (debit-ulterieur.md) — the merchant's
    // back office stands in here, calling POST /api/sessions/{sessionId}/debit through this demo's backend.
    const debitBox = document.getElementById("merchant-debit");
    if (debitBox) debitBox.hidden = !(detail.status === "PAID" && sessionStorage.getItem(ALIAS_KEY) === "guarantee");
  });
  document.getElementById("merchant-debit-button")?.addEventListener("click", () => {
    const input = document.getElementById("merchant-debit-amount");
    const result = document.getElementById("merchant-debit-result");
    if (!(input instanceof HTMLInputElement) || result === null) return;
    const amount = Math.round(Number(input.value) * 100);
    result.textContent = "…";
    void fetch("./m11/debit", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ amount }),
    })
      .then(async (response) => {
        const body = (await response.json()) as {
          code?: string;
          message?: string;
          debit?: { transaction: { status: { kind: string; statusMessage?: string } } };
        };
        const status = body.debit?.transaction.status;
        result.textContent =
          status !== undefined
            ? `${response.status} — débit ${status.kind}${status.statusMessage !== undefined ? ` (${status.statusMessage})` : ""} : le verdict arrive aussi par le webhook debit.`
            : `${response.status} — ${body.code ?? ""} ${body.message ?? ""}`;
      })
      .catch(() => {
        result.textContent = "Le backend marchand n'a pas répondu.";
      });
  });

  session.sessionClient = client;

  const setLocale = (locale: string): void => session.setAttribute("locale", locale);
  document.getElementById("locale-fr")?.addEventListener("click", () => setLocale("fr"));
  document.getElementById("locale-en")?.addEventListener("click", () => setLocale("en"));

  const reset = (): void => {
    // The alias and schedule choices survive a reset: they describe what the SHOP sells, not where the payer
    // is in its journey — clearing it would silently undo the box the tester just ticked.
    const alias = sessionStorage.getItem(ALIAS_KEY);
    const payments = sessionStorage.getItem(PAYMENTS_KEY);
    sessionStorage.clear();
    if (alias !== null) sessionStorage.setItem(ALIAS_KEY, alias);
    if (payments !== null) sessionStorage.setItem(PAYMENTS_KEY, payments);
    location.href = "./m11.html";
  };
  document.querySelector('[data-testid="session-reset"]')?.addEventListener("click", reset);
  document.querySelector('[data-testid="merchant-restart"]')?.addEventListener("click", reset);
}

void main().catch((error: unknown) => {
  console.error(error);
});
